/* Shared constants + helpers for the product screens (list, add, edit, details, bulk upload). */

export const API = process.env.REACT_APP_API_URL;

export const LANGS = [
    { code: "en", label: "English" },
    { code: "hi", label: "हिंदी (Hindi)" },
    { code: "es", label: "Español (Spanish)" },
    { code: "zh", label: "中文 (Chinese)" },
];
export const LANG_NAME = { en: "English", hi: "Hindi", es: "Spanish", zh: "Chinese" };

/* ---------- multilingual helpers ---------- */
export const emptyML = () => ({ en: "", hi: "", es: "", zh: "" });

/** Server value (string | {en,hi,es,zh} | undefined) -> editable object. */
export const toML = (f) => {
    if (!f) return emptyML();
    if (typeof f === "string") return { ...emptyML(), en: f };
    return { en: f.en || "", hi: f.hi || "", es: f.es || "", zh: f.zh || "" };
};

/** Fill any missing translation with the English text (what the old forms did on save). */
export const fillML = (ml) => {
    const en = String(ml?.en || "").trim();
    const pick = (l) => String(ml?.[l] || "").trim() || en;
    return { en, hi: pick("hi"), es: pick("es"), zh: pick("zh") };
};

/** Text for display in a language, falling back to English. */
export const mlText = (f, lang = "en") => {
    if (!f) return "";
    if (typeof f === "string") return f;
    if (typeof f === "object") return f[lang] || f.en || Object.values(f).find(Boolean) || "";
    return "";
};

export const asArray = (d, key) => (Array.isArray(d) ? d : Array.isArray(d?.[key]) ? d[key] : []);

/* ---------- defaults the catalogue schema requires (same values the old Add form hard-coded) ---------- */
export const DEFAULT_ORIGIN = { en: "Made in India", hi: "भारत में निर्मित", es: "Hecho en India", zh: "印度制造" };
export const DEFAULT_RETURN_POLICY = {
    en: "7-day replacement for damages",
    hi: "क्षति के लिए 7 दिन की प्रतिस्थापन",
    es: "Reemplazo de 7 días por daños",
    zh: "损坏7天更换",
};
export const DEFAULT_WARRANTY = {
    hasWarranty: true,
    warrantyDays: "30 days",
    description: {
        en: "Manufacturing defects covered",
        hi: "निर्माण दोष कवर",
        es: "Defectos de fabricación cubiertos",
        zh: "制造缺陷保修",
    },
};
export const DEFAULT_TRADE_INFO = {
    paymentTerms: { en: "Advance Payment (100%)", hi: "अग्रिम भुगतान (100%)", es: "Pago por adelantado (100%)", zh: "预付款 (100%)" },
    deliveryTime: { en: "5-7 business days", hi: "5-7 कार्य दिवस", es: "5-7 días hábiles", zh: "5-7个工作日" },
    supplyAbility: { en: "500 pieces/month", hi: "प्रति माह 500 टुकड़े", es: "500 piezas/mes", zh: "每月500件" },
    mainDomesticMarket: { en: "All India", hi: "संपूर्ण भारत", es: "Toda la India", zh: "全印度" },
};

/* ---------- formatting ---------- */
export const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

export const fmtMoney = (n) =>
    `₹${num(n).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "--";

export const fmtDateTime = (d) =>
    d ? `${fmtDate(d)}, ${new Date(d).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : "--";

export const errMsg = (err, fallback) => err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;

export const splitList = (s) =>
    String(s || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);

/* ---------- status styles ---------- */
export const STOCK_STATUS = {
    active: { label: "Active", pill: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
    inactive: { label: "Inactive", pill: "bg-slate-200 text-slate-700", dot: "bg-slate-400" },
    "out of stock": { label: "Out of stock", pill: "bg-red-100 text-red-600", dot: "bg-red-500" },
};

export const APPROVAL_STYLE = {
    Draft: "bg-slate-100 text-slate-600 border-slate-200",
    Submitted: "bg-sky-50 text-sky-700 border-sky-200",
    "Under Review": "bg-indigo-50 text-indigo-700 border-indigo-200",
    Correction: "bg-amber-50 text-amber-700 border-amber-200",
    "QC Hold": "bg-orange-50 text-orange-700 border-orange-200",
    Approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Published: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Unpublished: "bg-slate-100 text-slate-600 border-slate-200",
};

export const uploadedByLabel = (p) =>
    p?.uploadedBy === "supplier" ? p?.supplier?.businessName || p?.supplier?.fullName || "Supplier" : "Admin";